/**
 * Pause menu (08 §2.8): title, a compact controls list, RESUME and the way out. There is no
 * abort entry: recall is the abort. The list is read from the bindings each time the menu opens,
 * so it always shows the active scheme's keys.
 */

import './pauseOverlay.css';
import type { BindingsPort } from '../../input/bindings';
import { controlsList } from './controlsList';
import { el, show } from './dom';
import { actionsRow, buildOverlay, button } from './overlay';

export interface PauseOverlayOptions {
  leaveLabel?: string | undefined;
  /** Whose keys the controls list shows; the modern defaults when absent. */
  bindings?: BindingsPort | undefined;
  /** Daily Drop (04 §11): pausing must not be a way to study the playfield, so the dimmer is solid. */
  hidePlayfield?: boolean | undefined;
}

export class PauseOverlay {
  readonly el: HTMLElement;
  private readonly resume: HTMLButtonElement;
  private readonly controls: HTMLElement;
  private readonly bindings: BindingsPort | undefined;
  private listed = '';

  constructor(onResume: () => void, onLeave: () => void, options: PauseOverlayOptions = {}) {
    const { overlay, dialog } = buildOverlay('hud-pause', 'Paused');
    if (options.hidePlayfield === true) overlay.classList.add('hud-overlay-blackout');
    this.bindings = options.bindings;
    this.controls = el('div', 'hud-controls');
    this.resume = button('RESUME', onResume);
    dialog.append(
      el('div', 'hud-dialog-title', 'PAUSED'),
      this.controls,
      actionsRow(this.resume, button(options.leaveLabel ?? 'RESTART MISSION', onLeave)),
      el('div', 'hud-dialog-note', 'NO ABORT: RECALL THE OSPREY AND REACH THE LADDER'),
    );
    this.el = overlay;
    this.renderControls();
  }

  setVisible(visible: boolean): void {
    if (visible) this.renderControls();
    show(this.el, visible);
  }

  /** Keyboard players resume with Enter or Space. */
  focusResume(): void {
    this.resume.focus({ preventScroll: true });
  }

  private renderControls(): void {
    const rows = controlsList(this.bindings);
    const key = rows.map((row) => row.join('=')).join('|');
    if (key === this.listed) return;
    this.listed = key;
    this.controls.replaceChildren();
    for (const [keys, action] of rows) {
      this.controls.append(el('span', 'hud-key', keys), el('span', 'hud-control-name', action));
    }
  }
}
